import { useCallback, useMemo, useRef, useState } from 'react';
import { ZodFormattedError } from 'zod';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { ButtonAttributes } from '@celebration/design-system/dist/lib/button-group/props.types';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useTranslation } from 'react-i18next';
import { ItemsFooterType } from '@celebration/react/ClbModal/ClbModal.types';
import IUserFormFullPageFlowProps from './interfaces/IUserFormFullPageFlowProps';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import IUserFormDataErrors from './interfaces/IUserFormDataErrors';
import IUserFormData from './interfaces/IUserFormData';
import FullPageFlow from '../../../../common/components/full-page-flow/full-page-flow';
import UserFormSteps from './enums/user-form-steps';
import UserFormInfos from './components/user-form-infos/user-form-infos';
import UserFormService from './services/user-form.service';
import UserFormValidationService from './services/user-form.validation.service';
import UserOverview from '../../../../common/components/users/overview';
import IHandleUserFormChangeProps from './interfaces/IHandleUserChangeProps';
import UserAccountType from '../user-edit-form/enums/user-account-type.enum';
import Auth from '../../../../app/contexts/auth';
import ModalExitButtons from '../../../../common/components/modal';

function UserFormFullPageFlow({
    isUserFormOpened,
    setIsUserFormOpened,
}: Readonly<IUserFormFullPageFlowProps>) {
    const { t } = useTranslation();
    const { user, isThirdUser } = Auth.useAuth();
    const generalInfoValidatedOnce = useRef(false);
    const reactivateUser = useRef(false);

    const { setToast } = useToast();

    const [isLoading, setIsLoading] = useState<boolean>();

    const emptyErrors = useMemo(
        () => ({}) as ZodFormattedError<IUserFormDataErrors>,
        [],
    );
    const [errors, setErrors] = useState(emptyErrors);

    const [isExitModalOpen, setIsExitModalOpen] = useState(false);

    const [
        isShowingUserFormConfirmationModal,
        setIsShowingUserFormConfirmationModal,
    ] = useState(false);

    const [
        isShowingReactivateUserConfirmationModal,
        setIsShowingReactivateUserConfirmationModal,
    ] = useState(false);

    const emptyUserData = useMemo(
        () =>
            ({
                type: '0',
                userCarrier: isThirdUser()
                    ? {
                          carrierId: user?.carrier?.id,
                          name: user?.carrier?.name,
                          sulaHubId: user?.carrier?.sulahub_id,
                      }
                    : {
                          carrierId: '',
                      },
                systems: [
                    {
                        id: 'ontime-leilao',
                        label: 'OnTime Leilão',
                        number: 0,
                        isChecked: false,
                    },
                    {
                        id: 'smart-cargas',
                        label: 'Smart Cargas',
                        number: 1,
                        isChecked: false,
                    },
                ],
            }) as IUserFormData,
        [
            user?.carrier?.id,
            user?.carrier?.name,
            user?.carrier?.sulahub_id,
            isThirdUser,
        ],
    );

    const [userData, setUserData] = useState<IUserFormData>(emptyUserData);

    const [activeStep, setActiveStep] = useState<UserFormSteps>(
        UserFormSteps.info,
    );

    const validateInfos = useCallback((userData: IUserFormData) => {
        const infoResult = UserFormValidationService.validateInfos(userData);

        if (!infoResult || !infoResult.success) {
            setErrors(
                infoResult.errors as ZodFormattedError<IUserFormDataErrors>,
            );
            return false;
        }

        return true;
    }, []);

    const revalidateInfos = useCallback(
        (userData: IUserFormData) => {
            if (generalInfoValidatedOnce.current) {
                if (!validateInfos(userData)) return;

                setErrors(emptyErrors);
            }
        },
        [validateInfos, emptyErrors],
    );

    const validateUserEmail = useCallback(async (userData: IUserFormData) => {
        const userEmailResult =
            await UserFormValidationService.validateUserEmail(userData.email);

        if (
            !userEmailResult ||
            (!userEmailResult.success && !userEmailResult.isDeactivated)
        ) {
            setErrors(
                userEmailResult.errors as ZodFormattedError<IUserFormDataErrors>,
            );
            return false;
        }

        if (!userEmailResult.success && userEmailResult.isDeactivated) {
            reactivateUser.current = true;
        }

        return true;
    }, []);

    const validateUserFormInfoStep = useCallback(
        async (userData: IUserFormData) => {
            generalInfoValidatedOnce.current = true;

            if (!validateInfos(userData)) {
                return false;
            }

            if (!(await validateUserEmail(userData))) {
                return false;
            }
            setErrors(emptyErrors);

            return true;
        },
        [emptyErrors, validateInfos, validateUserEmail],
    );

    const closeUserFormHandler = useCallback(
        () => setIsExitModalOpen(true),
        [],
    );

    const closeExitModalHandler = useCallback(
        () => setIsExitModalOpen(false),
        [],
    );

    const showUserFormConfirmationModal = useCallback(() => {
        setIsShowingUserFormConfirmationModal(true);
    }, []);

    const showReactivateUserConfirmationModal = useCallback(() => {
        setIsShowingReactivateUserConfirmationModal(true);
    }, []);

    const goToPreviousStepHandler = useCallback(
        () => setActiveStep(activeStep - 1),
        [activeStep],
    );

    const goToReviewStepHandler = useCallback(async () => {
        setIsLoading(true);
        const validInfo = await validateUserFormInfoStep(userData);

        setIsLoading(false);

        if (validInfo) setActiveStep(UserFormSteps.review);
    }, [userData, validateUserFormInfoStep]);

    const closeModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
        setIsUserFormOpened(false);
        setActiveStep(UserFormSteps.info);
        setUserData(emptyUserData);
        setErrors(emptyErrors);
        generalInfoValidatedOnce.current = false;
    }, [emptyErrors, emptyUserData, setIsUserFormOpened]);

    const closeUserFormConfirmationModal = useCallback(() => {
        setIsShowingUserFormConfirmationModal(false);
    }, []);

    const closeReactivateUserConfirmationModal = useCallback(() => {
        reactivateUser.current = false;
        setIsShowingReactivateUserConfirmationModal(false);
    }, []);

    // eslint-disable-next-line consistent-return
    const fullPageFlowTitle = useMemo(() => {
        const stepTitle = t('NovoUsuario');

        // eslint-disable-next-line default-case
        switch (activeStep) {
            case UserFormSteps.info:
                return `${stepTitle} ${t('UsuarioEtapa1')}`;
            case UserFormSteps.review:
                return `${stepTitle} ${t('UsuarioEtapa2')}`;
        }
    }, [activeStep, t]);

    const headerFullPageFlowButtonList: ButtonAttributes[] = useMemo(() => {
        const headerButtons: ButtonAttributes[] = [];

        if (activeStep === UserFormSteps.info) {
            headerButtons.push({
                id: 'close-button',
                label: t('Fechar'),
                disabled: false,
                loading: false,
                title: t('UsuarioFecharCadastro'),
                ariaLabel: t('UsuarioFecharCadastro'),
                dataTestid: 'close-button',
                callback: closeUserFormHandler,
            });

            headerButtons.push({
                id: 'next-button',
                label: t('Avançar'),
                disabled: false,
                loading: false,
                title: t('UsuarioAvancarCadastro'),
                ariaLabel: t('UsuarioAvancarCadastro'),
                dataTestid: 'next-button',
                callback: goToReviewStepHandler,
            });
        }

        if (activeStep === UserFormSteps.review) {
            headerButtons.push({
                id: 'back-button',
                label: t('Voltar'),
                disabled: false,
                loading: false,
                title: t('UsuarioVoltarCadastro'),
                ariaLabel: t('UsuarioVoltarCadastro'),
                dataTestid: 'back-button',
                callback: goToPreviousStepHandler,
            });
            headerButtons.push({
                id: 'finish-button',
                label: isThirdUser()
                    ? t('UsuarioCriarSolicitacao')
                    : t('Finalizar'),
                disabled: false,
                loading: false,
                title: t('UsuarioFinalizarSolicitacao'),
                ariaLabel: t('UsuarioFinalizarSolicitacao'),
                dataTestid: 'finish-button',
                callback: !reactivateUser.current
                    ? showUserFormConfirmationModal
                    : showReactivateUserConfirmationModal,
            });
        }

        return headerButtons;
    }, [
        activeStep,
        closeUserFormHandler,
        goToReviewStepHandler,
        goToPreviousStepHandler,
        showUserFormConfirmationModal,
        showReactivateUserConfirmationModal,
        isThirdUser,
        t,
    ]);

    const submitUserRequestData = useCallback(async () => {
        if (reactivateUser.current) {
            setIsShowingReactivateUserConfirmationModal(false);
            reactivateUser.current = false;
        } else setIsShowingUserFormConfirmationModal(false);

        setIsLoading(true);

        let toastError = true;
        try {
            await UserFormService.saveUserRequest(userData, isThirdUser());

            toastError = false;

            if (isThirdUser())
                setToast(
                    'positive',
                    t('UsuarioSolicitacaoEnviada'),
                    t('UsuarioSolicitacaoEnviadaAprovacao'),
                );
            else setToast('positive', t('Sucesso'), t('UsuarioCriadoSucesso'));
        } catch (e: any) {
            if (toastError) {
                if (isThirdUser())
                    setToast('warning', t('UsuarioErroCriacao'), e.message);
                else setToast('negative', t('Erro'), e.message);
            }
        } finally {
            closeModalHandler();
            setIsLoading(false);
        }
    }, [closeModalHandler, setToast, userData, isThirdUser, t]);

    const buttonsModal: ItemsFooterType = [
        ModalExitButtons.exitButton(
            t,
            'UsuarioSairCadastro',
            closeModalHandler,
        ),
        ModalExitButtons.backButton(
            t,
            'UsuarioVoltarCadastro',
            setIsExitModalOpen,
        ),
    ];

    const renderExitConfirmationUserFormModal = () => {
        return (
            isExitModalOpen && (
                <ClbModal
                    title={t('UsuarioCancelaAdicao')}
                    open={isExitModalOpen}
                    onClose={closeExitModalHandler}
                    description={t('UsuarioSairCadastroPerdaDados')}
                    type="danger"
                    itemsFooter={buttonsModal}
                />
            )
        );
    };

    const renderUserFormConfirmationModal = () => {
        const action = isThirdUser() ? t('Solicitacao') : t('Criacao');

        return (
            isShowingUserFormConfirmationModal && (
                <ClbModal
                    title={`${t('Confirmar')} ${action} ${t('UsuarioDeUsuario')}?`}
                    open
                    onClose={closeUserFormConfirmationModal}
                    description={`${t('UsuarioMsgConfirmacaoContent1')} ${isThirdUser() ? t('solicitando') : t('Cadastrando')} ${t('UsuarioMsgConfirmacaoContent2')}`}
                    itemsFooter={[
                        {
                            key: 'new-user-confirmation-modal-yes-button',
                            id: 'new-user-confirmation-modal-yes-button',
                            label: t('Sim'),
                            disabled: false,
                            loading: false,
                            title: `${t('Confirmar')} ${action}`,
                            'data-testid':
                                'new-user-confirmation-modal-yes-button',
                            onClick: async () => {
                                await submitUserRequestData();
                            },
                            role: 'button',
                            size: 'md',
                        },
                        {
                            key: 'new-user-confirmation-modal-no-button',
                            id: 'new-user-confirmation-modal-no-button',
                            label: t('Não'),
                            disabled: false,
                            loading: false,
                            title: `${t('ConfirmarA')} ${action} ${t('UsuarioDeUsuario')}`,
                            'data-testid':
                                'new-user-confirmation-modal-no-button',
                            onClick: () => closeUserFormConfirmationModal(),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                        },
                    ]}
                />
            )
        );
    };

    const renderReactivateUserConfirmationModal = () => {
        return (
            isShowingReactivateUserConfirmationModal && (
                <ClbModal
                    title={t('ConfirmReactivation')}
                    open
                    onClose={closeReactivateUserConfirmationModal}
                    description={t('ConfirmReactivationDescription', {
                        userEmail: userData.email,
                    })}
                    itemsFooter={[
                        {
                            key: 'reactivate-user-confirmation-modal-yes-button',
                            id: 'reactivate-user-confirmation-modal-yes-button',
                            label: t('Sim'),
                            disabled: false,
                            loading: false,
                            title: `${t('Confirmar')}`,
                            'data-testid':
                                'reactivate-user-confirmation-modal-yes-button',
                            onClick: async () => {
                                await submitUserRequestData();
                            },
                            role: 'button',
                            size: 'md',
                        },
                        {
                            key: 'reactivate-user-confirmation-modal-no-button',
                            id: 'reactivate-user-confirmation-modal-no-button',
                            label: t('Não'),
                            disabled: false,
                            loading: false,
                            title: `${t('ConfirmarA')} ${t('UsuarioDeUsuario')}`,
                            'data-testid':
                                'reactivate-user-confirmation-modal-no-button',
                            onClick: () =>
                                closeReactivateUserConfirmationModal(),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                        },
                    ]}
                />
            )
        );
    };

    const handleUserFormChange = useCallback(
        (handleUserFormChangeProps: IHandleUserFormChangeProps) => {
            const updatedUserData = {
                ...userData,
                [handleUserFormChangeProps.property]:
                    handleUserFormChangeProps.newValue,
            };

            revalidateInfos(updatedUserData);
            setUserData(updatedUserData);
        },
        [userData, revalidateInfos],
    );

    return (
        <>
            <FullPageFlow
                title={fullPageFlowTitle}
                type="progress"
                opened={isUserFormOpened}
                onClbClose={closeUserFormHandler}
                steps={1}
                buttonsList={headerFullPageFlowButtonList}
                activeStep={activeStep}
                data-testid="full-page-flow"
            >
                {isLoading && <ClbLoading />}

                {isUserFormOpened &&
                    activeStep === UserFormSteps.info &&
                    !isLoading && (
                        <UserFormInfos
                            handleUserFormChange={handleUserFormChange}
                            userFormData={userData}
                            inputErrors={errors}
                        />
                    )}

                {isUserFormOpened &&
                    activeStep === UserFormSteps.review &&
                    !isLoading && (
                        <UserOverview
                            userData={userData}
                            userAccountType={UserAccountType.third}
                        />
                    )}
            </FullPageFlow>

            {renderExitConfirmationUserFormModal()}
            {renderUserFormConfirmationModal()}
            {renderReactivateUserConfirmationModal()}
        </>
    );
}

export default UserFormFullPageFlow;
